/**
 * BatchEnrichmentPanel Component
 *
 * Shows enrichment status and allows triggering batch AI enrichment
 * for products missing enrichment. Used after wizard completion when
 * enrichment was skipped to reduce onboarding time.
 *
 * Created: 2026-02-21
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sparkles,
  DollarSign,
  Clock,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Square,
  Zap
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { authFetch } from '@/utils/auth-fetch';

/**
 * A category the operator can target. `unenriched_count` is what the run
 * will actually process — NOT the site-wide figure the cost tile shows.
 */
interface EnrichmentCategory {
  category_path: string;
  unenriched_count: number;
  total_count: number;
}

interface CostBreakdown {
  inputTokens: number;
  outputTokens: number;
  inputCost: number;
  outputCost: number;
}

interface CostEstimate {
  amount: number;
  currency: string;
  model: string;
  provider: string;
  breakdown: CostBreakdown;
}

interface LatestBatch {
  id: string;
  status: string;
  total_products: number;
  processed_products: number;
  succeeded_products: number;
  created_at: string;
  started_at: string | null;
  progress_percent: number;
}

interface EnrichmentStatus {
  totalProducts: number;
  enrichedProducts: number;
  productsNeedingEnrichment: number;
  percentEnriched: number;
  hasActiveBatches: boolean;
  latestBatch: LatestBatch | null;
  costEstimate: CostEstimate;
  estimatedDuration: string;
  canStartEnrichment: boolean;
}

interface BatchEnrichmentPanelProps {
  siteId: string;
  onRefresh?: () => void;
}

export function BatchEnrichmentPanel({ siteId, onRefresh }: BatchEnrichmentPanelProps) {
  const [status, setStatus] = useState<EnrichmentStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [categories, setCategories] = useState<EnrichmentCategory[]>([]);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  // The server's per-run ceiling, SERVED not hardcoded — a second copy of the
  // number here would silently stop matching the day the backend's moves.
  const [maxProductsPerRun, setMaxProductsPerRun] = useState<number | null>(null);
  // Deliberately NOT defaulted to the first category. A default here would
  // reproduce the defect this control exists to remove: the operator must
  // choose, and until they do the start button stays disabled.
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  // task-9d8l7utxnj — stopping a running batch. Two-step by design: a single-click
  // stop on a paid, in-flight run is too easy to hit by accident, and the panel
  // already trained operators that a button here does nothing.
  // ⛔ THE ARMED JOB ID, NOT A BOOLEAN (#5206 codex BLOCKING). A boolean records THAT a
  // confirmation is open; it cannot record WHICH run the operator agreed to stop. See handleStop.
  const [confirmStop, setConfirmStop] = useState<string | null>(null);
  const [isStopping, setIsStopping] = useState(false);
  const [stopError, setStopError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      setError(null);
      const response = await authFetch(`/api/aeo-readiness/site/${siteId}/enrichment-estimate`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load enrichment status');
      }

      const result = await response.json();
      setStatus(result.data);
    } catch (err) {
      console.error('Failed to load enrichment status:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  const loadCategories = useCallback(async () => {
    try {
      setCategoriesError(null);
      const response = await authFetch(`/api/aeo-readiness/site/${siteId}/enrichment-categories`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load categories');
      }
      const result = await response.json();
      // ⛔ FAIL CLOSED ON A MISSING CEILING. Defaulting to "no limit" would let the
      // panel build a request the server can only reject, and defaulting to a
      // hardcoded 5000 would be the duplicate-constant bug this field exists to
      // avoid. Neither is safe to guess, so an absent ceiling is a load failure —
      // same surface as an absent category list, and just as loud.
      const ceiling = result.data?.maxProductsPerRun;
      if (!Number.isInteger(ceiling) || ceiling < 1) {
        throw new Error(
          `Server did not report a per-run product ceiling (got ${JSON.stringify(ceiling)}). ` +
          'Enrichment stays unavailable rather than guessing a batch size.'
        );
      }
      setMaxProductsPerRun(ceiling);
      setCategories(result.data?.categories || []);
    } catch (err) {
      // Surfaced, not swallowed: without this list the operator cannot choose
      // a target, and enrichment must stay unavailable rather than silently
      // falling back to a server-side pick.
      console.error('Failed to load enrichment categories:', err);
      setCategoriesError(err instanceof Error ? err.message : 'An error occurred');
    }
  }, [siteId]);

  useEffect(() => {
    loadStatus();
    loadCategories();
  }, [loadStatus, loadCategories]);

  // Poll for updates if there's an active batch
  useEffect(() => {
    if (!status?.hasActiveBatches) return;

    const interval = setInterval(() => {
      loadStatus();
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(interval);
  }, [status?.hasActiveBatches, loadStatus]);

  // What the chosen category will actually process. The cost tile above shows
  // a SITE-WIDE figure; these are different numbers and the dialog must quote
  // this one, because this is the one the run uses.
  const selectedCategoryCount =
    categories.find(c => c.category_path === selectedCategory)?.unenriched_count ?? 0;

  // ⛔ A CATEGORY CAN BE BIGGER THAN ONE BATCH, AND TWO REALLY ARE (worst: 11,644
  // unenriched, measured fleet-wide 2026-08-03). start-enrichment 400s above the
  // ceiling and deliberately does NOT split — fanning one confirmed action out
  // into several jobs would spend more than the single estimate the operator
  // approved. So the PANEL offers a bounded first slice instead of building a
  // request that can only fail. Each completed run shrinks unenriched_count, so
  // repeating drains the category.
  const runSize =
    maxProductsPerRun === null ? 0 : Math.min(selectedCategoryCount, maxProductsPerRun);
  const isClamped = runSize > 0 && runSize < selectedCategoryCount;

  const handleStartEnrichment = async () => {
    setIsStarting(true);
    setStartError(null);

    try {
      const response = await authFetch(`/api/aeo-readiness/site/${siteId}/start-enrichment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userConfirmedCost: true,
          categoryPath: selectedCategory,
          // Bound the run at exactly what the operator was shown — which is
          // runSize, NOT the category total, when the category is larger than one
          // batch. If the cohort grows between the dialog opening and the click,
          // the run still cannot exceed the number they confirmed.
          maxProducts: runSize > 0 ? runSize : undefined
        })
      });

      // ⛔ response.ok is NOT success. This endpoint can return HTTP 200 with
      // { success: false, mode: 'none' } — a request that was accepted and
      // enriched nothing. Closing the dialog on `ok` alone reports work that
      // did not happen, which is the same fail-open we just removed from the
      // productIds path. Parse the body and require success.
      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.success) {
        throw new Error(
          result?.error ||
          result?.message ||
          `Enrichment did not start (mode: ${result?.mode ?? 'unknown'}).`
        );
      }

      // Success - close dialog and refresh
      setShowConfirmDialog(false);
      loadStatus();
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Failed to start enrichment:', err);
      setStartError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsStarting(false);
    }
  };

  /**
   * Stop the running enrichment batch (task-9d8l7utxnj).
   *
   * The job id comes from `status.latestBatch.id`, which the status poll already
   * supplies — NOT from the start response, which this component discards. That
   * matters: a stop that only worked in the tab which started the run would be
   * useless to the person who actually needs it, who has usually reloaded or is
   * somebody else entirely.
   */
  const handleStop = async () => {
    // ⛔⛔ THE ID THE OPERATOR AGREED TO STOP, CAPTURED WHEN THE PROMPT WAS ARMED — NOT re-read
    //   from `status` here. #5206 codex BLOCKING, and it is a destructive wrong-target bug rather
    //   than a tidiness point: this panel POLLS. Between "Stop this run" and "Confirm stop" the
    //   poll can replace `latestBatch` — the reviewed run finishes and the next one starts — and
    //   reading the id at confirmation time would silently cancel a run the operator never looked
    //   at. The confirmation would be honest about the ACT and wrong about the TARGET.
    // 🔑 A boolean can only record THAT a prompt is open. Binding the prompt to the job means the
    //   state has to BE the job.
    const jobId = confirmStop;
    if (!jobId) {
      setStopError('No running batch to stop — the status may have just changed. Refresh and check.');
      setConfirmStop(null);
      return;
    }

    // ⚠️ AND CAPTURING IS NOT ENOUGH ON ITS OWN. If the active run changed while the prompt was
    //   open, the captured id is now stale: cancelling it would either hit an already-finished job
    //   (a confusing 409) or, worse, read as "the stop worked" while the run the operator can SEE
    //   keeps going. Refuse and make them re-confirm against what is actually on screen. This is
    //   the same rule as the backend's guarded writes — do not act on state that moved underneath.
    const currentId = status?.latestBatch?.id;
    if (currentId !== jobId) {
      setStopError(
        'The running batch changed while this confirmation was open, so nothing was stopped. '
        + 'Check the run shown now and confirm again if you still want to stop it.',
      );
      setConfirmStop(null);
      return;
    }

    setIsStopping(true);
    setStopError(null);
    try {
      const response = await authFetch(
        `/api/aeo-readiness/site/${siteId}/enrichment/${jobId}/cancel`,
        { method: 'POST' }
      );

      // ⛔ response.ok is NOT success — same rule as the start path above. The
      // cancel endpoint answers 409 with the REAL reason (already terminal, already
      // finished at the provider, status write raced a sweep), and every one of
      // those is a stop that did NOT happen. Surfacing a green here would rebuild
      // the exact defect this control was added to remove.
      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.success) {
        throw new Error(result?.error || `Stop failed (HTTP ${response.status}).`);
      }

      setConfirmStop(null);
      loadStatus();
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Failed to stop enrichment batch:', err);
      setStopError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsStopping(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-muted-foreground">
            <RefreshCw className="h-4 w-4 animate-spin" />
            Loading enrichment status...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200">
        <CardContent className="pt-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button onClick={loadStatus} variant="outline" size="sm" className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!status) return null;

  // ⛔⛔ THE ACTIVE-BATCH CARD IS CHECKED FIRST, AND THE ORDER IS THE WHOLE POINT (#5206 codex
  //   BLOCKING). It used to sit BELOW the "All Products Enriched" shortcut, and the two are not
  //   mutually exclusive: `percentEnriched` is derived from `extracted_attributes` alone
  //   (routes/aeo-readiness.js:433-434), which the EXTRACTION phase fills in for every product —
  //   so a job that has moved on to BENEFIT SCORING reads as 100% while its second OpenAI batch
  //   is still running and still billing.
  // 🔑 The shortcut therefore replaced the active-batch card, and the Stop control lives INSIDE
  //   that card — so the panel hid the only way to stop a live paid run, and told the operator
  //   the work was finished. That is precisely the unstoppable-phase defect this PR exists to
  //   remove, one phase further along: before, nothing could stop a batch; here, the thing that
  //   can stop it is hidden exactly when the run is at its most expensive.
  // ⇒ An active batch always wins. "All products enriched" is a statement about EXTRACTION and it
  //   must not be allowed to speak for the JOB. Expressed as a guard on the shortcut rather than
  //   by relocating the active-batch JSX below: same resulting precedence, a one-condition diff,
  //   and no risk of a large block move altering markup that is not the subject of this fix.
  // All products enriched — only when nothing is still running.
  if (status.percentEnriched === 100 && !status.hasActiveBatches) {
    return (
      <Card className="border-green-200 bg-green-50 dark:bg-green-950/20">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
            <CheckCircle2 className="h-5 w-5" />
            All Products Enriched
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-green-600 dark:text-green-500">
            All {status.totalProducts.toLocaleString()} products have been AI enriched with attributes.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Active batch in progress
  if (status.hasActiveBatches && status.latestBatch) {
    const batch = status.latestBatch;
    const progress = batch.progress_percent || 0;

    return (
      <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950/20">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-blue-700 dark:text-blue-400">
            <RefreshCw className="h-5 w-5 animate-spin" />
            Enrichment in Progress
          </CardTitle>
          <CardDescription className="text-blue-600">
            AI enrichment batch is processing your products
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-blue-700 dark:text-blue-400">Progress</span>
              <span className="text-blue-600">
                {batch.processed_products?.toLocaleString() || 0} / {batch.total_products?.toLocaleString() || 0} products
              </span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Status</span>
              <div className="font-medium capitalize">{batch.status}</div>
            </div>
            <div>
              <span className="text-muted-foreground">Succeeded</span>
              <div className="font-medium text-green-600">{batch.succeeded_products || 0}</div>
            </div>
            <div>
              <span className="text-muted-foreground">Started</span>
              <div className="font-medium">
                {batch.started_at
                  ? new Date(batch.started_at).toLocaleTimeString()
                  : 'Pending'}
              </div>
            </div>
          </div>

          {/* task-9d8l7utxnj — until this existed there was NO way to stop a running
              enrichment batch from anywhere in the product. The only control labelled
              "Cancel" is the confirmation dialog's dismiss button (further down this
              file), which stops nothing.

              ⚠️ DELIBERATELY LABELLED "Stop this run", NOT "Cancel". A second control
              reading "Cancel" on the same panel as a dialog-dismiss "Cancel" is how the
              original confusion was built; the whole defect here is a control whose name
              promises something it does not do. */}
          {stopError && (
            <Alert variant="destructive">
              {/* The server's real message, verbatim — it distinguishes "already
                  finished at the provider", "already terminal", and "the status write
                  raced a sweep", and the operator needs to know WHICH. */}
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{stopError}</AlertDescription>
            </Alert>
          )}
          {/* ⛔⛔ THE STOP CONTROL IS NOT OFFERED FOR A STATE IT CANNOT STOP (#5206 codex BLOCKING).
              `processing_results` counts as an ACTIVE batch (AEOReadinessService:2108), so this card
              renders for it — but by then the OpenAI batch has COMPLETED, so a stop could only reach
              the provider, be refused, and come back as "already finished". Offering a button whose
              every outcome is a refusal rebuilds the exact defect this control was added to remove:
              a control that looks like it stops something and does not.
              🔑 And there is nothing left to save: the provider work is finished and already paid
              for, so the only thing still running is the local ingest of results we own. Aborting
              that would leave products half-enriched — a data-consistency hazard traded for a cost
              saving that does not exist. So the card SAYS SO rather than silently dropping the
              button, because an operator who came here to stop a run needs to know why they can't. */}
          <div className="flex items-center justify-between gap-3 border-t border-blue-200 pt-3 dark:border-blue-900">
            {batch.status === 'processing_results' ? (
              <span className="text-xs text-muted-foreground">
                This run can no longer be stopped — the provider has finished and its cost is
                already committed. The remaining work is writing the results, which is left to
                finish so products are not left partially enriched.
              </span>
            ) : (
              <>
                <span className="text-xs text-muted-foreground">
                  Products already enriched by this run are kept. Stopping cancels the
                  remaining work at the provider.
                </span>
            {confirmStop ? (
              <div className="flex shrink-0 items-center gap-2">
                <Button variant="destructive" size="sm" onClick={handleStop} disabled={isStopping}>
                  {isStopping ? 'Stopping…' : 'Confirm stop'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmStop(null)}
                  disabled={isStopping}
                >
                  Keep running
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => { setStopError(null); setConfirmStop(batch.id); }}
              >
                <Square className="mr-2 h-3 w-3" />
                Stop this run
              </Button>
            )}
              </>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Products need enrichment - show trigger panel
  return (
    <>
      <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
            <Sparkles className="h-5 w-5" />
            AI Product Enrichment Available
          </CardTitle>
          <CardDescription className="text-amber-600">
            {status.productsNeedingEnrichment.toLocaleString()} products need AI attribute extraction
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Progress bar */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-muted-foreground">Current enrichment</span>
              <span className="font-medium">
                {status.enrichedProducts.toLocaleString()} / {status.totalProducts.toLocaleString()} ({status.percentEnriched}%)
              </span>
            </div>
            <Progress value={status.percentEnriched} className="h-2" />
          </div>

          {/* Cost estimate */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-start gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <div className="text-xs text-muted-foreground">Estimated Cost</div>
                <div className="font-semibold">
                  ${status.costEstimate.amount.toFixed(2)}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Clock className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <div className="text-xs text-muted-foreground">Duration</div>
                <div className="font-semibold">{status.estimatedDuration}</div>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Zap className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <div className="text-xs text-muted-foreground">Model</div>
                <div className="font-semibold text-sm">
                  {status.costEstimate.model}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Sparkles className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <div className="text-xs text-muted-foreground">Products</div>
                <div className="font-semibold">
                  {status.productsNeedingEnrichment.toLocaleString()}
                </div>
              </div>
            </div>
          </div>

          {/* Provider badge */}
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">
              {status.costEstimate.provider === 'vertexai' ? 'Google Vertex AI' : 'OpenAI'}
            </Badge>
            <span className="text-xs text-muted-foreground">
              50% cheaper than standard API rates
            </span>
          </div>

          {/* Category selector — the operator picks the target. */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Category to enrich</label>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a category…" />
              </SelectTrigger>
              <SelectContent>
                {categories.filter(c => c.unenriched_count > 0).map(c => (
                  <SelectItem key={c.category_path} value={c.category_path}>
                    {c.category_path} — {c.unenriched_count.toLocaleString()} to enrich
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {categoriesError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Could not load categories: {categoriesError}. Enrichment is unavailable until this loads —
                  it will not run against a category chosen for you.
                </AlertDescription>
              </Alert>
            )}
            {!categoriesError && categories.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No categories found. Products whose feed carries no category cannot be enriched by category.
              </p>
            )}
          </div>

          {/* Start button */}
          <Button
            onClick={() => setShowConfirmDialog(true)}
            className="w-full"
            disabled={!status.canStartEnrichment || !selectedCategory || runSize < 1}
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Start AI Enrichment
          </Button>
          {!selectedCategory && (
            <p className="text-xs text-muted-foreground text-center">
              Choose a category first — enrichment no longer picks one for you.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start AI Product Enrichment?</DialogTitle>
            <DialogDescription>
              This enriches <strong>{runSize.toLocaleString()}</strong> unenriched products in{' '}
              <strong>{selectedCategory}</strong>, using {status.costEstimate.model}. These exact products are
              selected now and sent to the batch — the run cannot substitute a different set.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Named target, in plain English, before anything runs. The old
                dialog quoted the SITE-WIDE unenriched count while the run was
                category-scoped — so the number confirmed described a different
                scope than the number processed. */}
            <div className="p-3 border rounded-lg text-sm">
              <div className="text-muted-foreground">Category</div>
              <div className="font-medium break-words">{selectedCategory}</div>
              <div className="mt-2 text-muted-foreground">Products to enrich</div>
              <div className="font-medium">{runSize.toLocaleString()}</div>
              {/* ⚠️ Disclosed, never silent. A category larger than one batch is
                  run in slices, and an operator who was shown the category total
                  but charged for a slice (or vice versa) cannot reconcile either
                  number afterwards. Say which one this run is. */}
              {isClamped && (
                <p className="mt-2 text-xs">
                  This category has {selectedCategoryCount.toLocaleString()} unenriched products — more
                  than one batch can hold. This run covers the first{' '}
                  {runSize.toLocaleString()}. Run it again afterwards to continue; each run picks up
                  where the last one stopped.
                </p>
              )}
              {status.productsNeedingEnrichment !== selectedCategoryCount && (
                <p className="mt-2 text-xs text-muted-foreground">
                  This site has {status.productsNeedingEnrichment.toLocaleString()} unenriched products in
                  total.
                </p>
              )}
            </div>

            {/* ⚠️ THIS WARNING IS GONE BECAUSE THE DEFECT IS FIXED, NOT BECAUSE IT
                WAS SOFTENED. It used to say the count was an estimate, because the
                count and the cohort came from different keys:

                  displayed here : knowledge_base_entries.metadata->>'category',
                                   matched EXACTLY
                  selected by run: product_feed_items.category, matched as
                                   `= path OR LIKE path || '%'`

                Measured on DiveInn 2026-08-03, 3 of 7 categories disagreed in BOTH
                directions ("… > Wetsuits" showed 29 and selected 4,487).

                ⇒ Labelling that made the dialog HONEST, not CORRECT — a disclosed
                mismatch still spends money on products the operator did not select.
                start-enrichment now resolves the ids itself with the SAME predicate
                as this count and passes them to the batch service as an explicit
                cohort, so the two queries cannot diverge: there is only one of them.
                ⛔ If that ever regresses to passing a bare categoryPath, restore this
                warning — a stale reassurance is worse than the original defect. */}

            {/* ⛔ SCOPE MISMATCH, LABELLED RATHER THAN HIDDEN. These two figures come
                from `status`, which is computed SITE-WIDE
                (status.productsNeedingEnrichment products), while the count above is
                for the ONE selected category. Showing an unlabelled number in a
                confirm dialog means it is read as the price of the thing being
                confirmed — the same defect class as the product count this dialog
                already had to stop misreporting.
                ⇒ Until a category-scoped estimate exists they are labelled as
                SITE-WIDE UPPER BOUNDS, which is what they actually are. */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="p-3 bg-muted rounded-lg">
                <div className="text-muted-foreground">Estimated Cost</div>
                <div className="text-2xl font-bold">${status.costEstimate.amount.toFixed(2)}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Whole site — upper bound, not this category
                </div>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <div className="text-muted-foreground">Estimated Time</div>
                <div className="text-2xl font-bold">{status.estimatedDuration}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Whole site — upper bound, not this category
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Cost and time above are for all{' '}
              <strong>{status.productsNeedingEnrichment.toLocaleString()}</strong> unenriched products on
              this site, not for the{' '}
              <strong>{runSize.toLocaleString()}</strong> in{' '}
              <strong>{selectedCategory}</strong>. This run should cost less — treat them as a ceiling.
            </p>

            <div className="text-sm text-muted-foreground">
              <ul className="list-disc pl-4 space-y-1">
                <li>Processing runs in the background - you can close this page</li>
                <li>Progress will be shown in the AEO Readiness Dashboard</li>
                <li>You'll be notified when enrichment completes</li>
              </ul>
            </div>

            {startError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{startError}</AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)} disabled={isStarting}>
              Cancel
            </Button>
            <Button onClick={handleStartEnrichment} disabled={isStarting}>
              {isStarting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Confirm & Start
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
